/* eslint-disable react/display-name */
/* eslint-disable no-unused-vars */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { isArray } from 'lodash';

import { ResizeHandle } from '../ThreeColumnLayout/ResizeHandle';
import { SideBar } from '../ThreeColumnLayout/SideBar';
import IconCollapse from 'svgIcons/MotionPortalIcons/IconCollapse';
import IconExpand from 'svgIcons/MotionPortalIcons/IconExpand';
import './style.scss';

// Fixed collapsed widths (px): padding-left (style.scss) + 48px box
const LEFT_COLLAPSED_WIDTH_PX = 60; // 12px gutter + 48px box
const CENTER_COLLAPSED_WIDTH_PX = 52; // 4px gutter + 48px box
// Min widths before horizontal scroll
const LEFT_MIN_WIDTH_PX = 220;
const CENTER_MIN_WIDTH_PX = 240;
// Row's overall min-width floor
const ROW_MIN_WIDTH_PX = 860;

const ThreeColumnResizableLayoutV2 = React.memo(({
    leftPanelDefaulSize = 25,
    leftPanelMinSize = 10,
    leftPanelCollapsedSize = 3,
    leftPanelCollapsedIcon = <IconExpand width={16} height={16} />,
    leftPanelExpandedIcon = <IconCollapse width={16} height={16} />,
    centerPanelMinSize = 10,
    centerPanelDefaultSize = 25,
    centerPanelCollapsedSize = 3,
    centerPanelCollapsedIcon = <IconExpand width={16} height={16} />,
    centerPanelExpandedIcon = <IconCollapse width={16} height={16} />,
    leftPanelMaxSize,
    centerPanelMaxSize,
    // Caps left/center to a constant px width on wide screens; freed space flows to the right panel
    leftPanelMaxWidthPx,
    centerPanelMaxWidthPx,
    rightPanelMinSize = 20,
    rightPanelDefaultSize = 50,
    leftPanelHeader = '',
    centerPanelHeader = '',
    onLeftPanelCollapse,
    onCenterPanelCollapse,
    children,
    onResizeLeftPanel = () => { },
    onResizeCenterPanel = () => { },
    onResizeRightPanel = () => { },
    mainLayoutclass,
    isForHierarchyView,
    isForConditionOverview,
    conditionIndicesView,
    setConditionIndicesView,
    requestHierarchyData,
    lastUpdatedTimestamp,
    assetsLastUpdatedTimestamp,
    activeTab,
    isCenterCollapsible = true,
    isLeftCollapsible = true,
    leftPanelContentPadding,
}) => {
    const [leftPanelOpen, setLeftPanelOpen] = useState(true);
    const [centerPanelOpen, setCenterPanelOpen] = useState(true);
    // Collapsed sizes (%), recomputed from container width via ResizeObserver below
    const [leftCollapsedPct, setLeftCollapsedPct] = useState(leftPanelCollapsedSize);
    const [centerCollapsedPct, setCenterCollapsedPct] = useState(centerPanelCollapsedSize);
    // Max sizes (%) derived from leftPanelMaxWidthPx/centerPanelMaxWidthPx, if provided
    const [leftMaxWidthPct, setLeftMaxWidthPct] = useState(undefined);
    const [centerMaxWidthPct, setCenterMaxWidthPct] = useState(undefined);
    // Seeded from window width to avoid collapse-snap
    const estimateFloorPct = (minWidthPx, fallbackPct) => {
        if (!globalThis.window?.innerWidth) { return fallbackPct; }
        return Math.max(fallbackPct, Math.min(50, minWidthPx / globalThis.window.innerWidth * 100));
    };
    const [leftFloorPct, setLeftFloorPct] = useState(() => estimateFloorPct(LEFT_MIN_WIDTH_PX, leftPanelMinSize));
    const [centerFloorPct, setCenterFloorPct] = useState(() => estimateFloorPct(CENTER_MIN_WIDTH_PX, centerPanelMinSize));

    const wrapperRef = useRef(null);
    // Measures row's actual width
    const groupWidthRef = useRef(null);
    const panelGroupRef = useRef(null);
    // Last known genuine expanded sizes, used to restore/redistribute on toggle
    const leftExpandedSizeRef = useRef(leftPanelDefaulSize);
    const centerExpandedSizeRef = useRef(centerPanelDefaultSize);
    // Ignores onResize while we're applying our own setLayout() below
    const isProgrammaticLayoutRef = useRef(false);
    // Set by a button click, consumed by the effect below once the state change has rendered
    const pendingApplyRef = useRef(null);

    const leftContent = useMemo(() => children && isArray(children) ? children[0] : children, [children]);
    const centerContent = useMemo(() => children && children?.length > 1 && children[1], [children]);
    const rightContent = useMemo(() => children && children?.length > 2 && children[2], [children]);

    // Measure container width so collapsed/max-width-pinned panels stay an exact pixel size
    useEffect(() => {
        const el = groupWidthRef.current;
        if (!el || typeof ResizeObserver === 'undefined') { return; }
        const updateCollapsedPct = () => {
            const width = el.getBoundingClientRect().width;
            if (width > 0) {
                setLeftCollapsedPct(LEFT_COLLAPSED_WIDTH_PX / width * 100);
                setCenterCollapsedPct(CENTER_COLLAPSED_WIDTH_PX / width * 100);
                setLeftMaxWidthPct(leftPanelMaxWidthPx ? leftPanelMaxWidthPx / width * 100 : undefined);
                setCenterMaxWidthPct(centerPanelMaxWidthPx ? centerPanelMaxWidthPx / width * 100 : undefined);
                setLeftFloorPct(Math.min(50, LEFT_MIN_WIDTH_PX / width * 100));
                setCenterFloorPct(Math.min(50, CENTER_MIN_WIDTH_PX / width * 100));
            }
        };
        updateCollapsedPct();
        const observer = new ResizeObserver(updateCollapsedPct);
        observer.observe(el);
        return () => observer.disconnect();
    }, [leftPanelMaxWidthPx, centerPanelMaxWidthPx]);

    // The more restrictive of the plain percentage max and the pixel-derived max, if any
    const rawLeftMaxSize = leftMaxWidthPct === undefined ? leftPanelMaxSize
        : Math.min(leftPanelMaxSize ?? 100, leftMaxWidthPct);
    const rawCenterMaxSize = centerMaxWidthPct === undefined ? centerPanelMaxSize
        : Math.min(centerPanelMaxSize ?? 100, centerMaxWidthPct);
    // Never below pixel floor
    const effectiveLeftMinSize = Math.max(leftPanelMinSize, leftFloorPct);
    const effectiveCenterMinSize = Math.max(centerPanelMinSize, centerFloorPct);
    // Max never below min
    const effectiveLeftMaxSize = Math.max(rawLeftMaxSize ?? 100, effectiveLeftMinSize);
    const effectiveCenterMaxSize = Math.max(rawCenterMaxSize ?? 100, effectiveCenterMinSize);
    // Avoids collapse-snap on mount
    const leftInitialSize = Math.max(leftPanelDefaulSize, effectiveLeftMinSize);
    const centerInitialSize = Math.max(centerPanelDefaultSize, effectiveCenterMinSize);
    const rightInitialSize = Math.max(rightPanelMinSize, 100 - leftInitialSize - centerInitialSize);

    // Explicitly sets all 3 panel sizes (button-triggered toggles only — drag already redistributes
    // naturally). growCenterBonus grows center when left collapses; right always takes the remainder.
    const applyLayout = useCallback((nextLeftOpen, nextCenterOpen, growCenterBonus = false) => {
        const group = panelGroupRef.current;
        if (!group || !rightContent) { return; }

        const left = nextLeftOpen ? leftExpandedSizeRef.current : leftCollapsedPct;
        let center = nextCenterOpen ? centerExpandedSizeRef.current : centerCollapsedPct;

        if (growCenterBonus && nextCenterOpen) {
            const freed = leftExpandedSizeRef.current - leftCollapsedPct;
            center = Math.min(centerExpandedSizeRef.current + freed, effectiveCenterMaxSize ?? 100);
        }

        const right = 100 - left - center;

        isProgrammaticLayoutRef.current = true;
        group.setLayout([left, center, right]);
        // Double rAF: onResize notifications from setLayout can lag a frame
        requestAnimationFrame(() => {
            requestAnimationFrame(() => { isProgrammaticLayoutRef.current = false; });
        });
    }, [leftCollapsedPct, centerCollapsedPct, rightContent, effectiveCenterMaxSize]);

    // Runs the pending redistribution only after the open/closed state has rendered; drag-driven
    // changes leave pendingApplyRef null and are skipped
    useEffect(() => {
        if (!pendingApplyRef.current) { return; }
        const { growCenterBonus } = pendingApplyRef.current;
        pendingApplyRef.current = null;
        applyLayout(leftPanelOpen, centerPanelOpen, growCenterBonus);
    }, [leftPanelOpen, centerPanelOpen, applyLayout]);

    const handleLeftResize = useCallback((size) => {
        onResizeLeftPanel(size);
        // Skip our own setLayout-triggered resizes; only capture genuine drags
        if (!isProgrammaticLayoutRef.current && leftPanelOpen && centerPanelOpen && size >= effectiveLeftMinSize) {
            leftExpandedSizeRef.current = size;
        }
    }, [onResizeLeftPanel, effectiveLeftMinSize, leftPanelOpen, centerPanelOpen]);

    const handleCenterResize = useCallback((size) => {
        onResizeCenterPanel(size);
        if (!isProgrammaticLayoutRef.current && leftPanelOpen && centerPanelOpen && size >= effectiveCenterMinSize) {
            centerExpandedSizeRef.current = size;
        }
    }, [onResizeCenterPanel, effectiveCenterMinSize, leftPanelOpen, centerPanelOpen]);

    const handleRightResize = useCallback((size) => {
        onResizeRightPanel(size);
    }, [onResizeRightPanel]);

    // Button-triggered toggle (drag goes through Panel's onCollapse/onExpand instead)
    const onLeftPanelToggle = useCallback(() => {
        const next = !leftPanelOpen;
        pendingApplyRef.current = { growCenterBonus: leftPanelOpen }; // true when this click collapses it
        setLeftPanelOpen(next);
        onLeftPanelCollapse?.(!next);
    }, [leftPanelOpen, onLeftPanelCollapse]);

    const onCenterPanelToggle = useCallback(() => {
        const next = !centerPanelOpen;
        pendingApplyRef.current = { growCenterBonus: false };
        setCenterPanelOpen(next);
        onCenterPanelCollapse?.(!next);
    }, [centerPanelOpen, onCenterPanelCollapse]);

    return (
        <div ref={wrapperRef} className='three-column-v2-scroll-container'>
            <div ref={groupWidthRef} className='three-column-v2-min-width-wrapper'>
                <PanelGroup ref={panelGroupRef} direction='horizontal' className={`three-column-v2-panel-group ${mainLayoutclass || ''}`}>
                    <Panel
                        id='left-panel'
                        defaultSize={leftInitialSize}
                        collapsible={isLeftCollapsible}
                        // Pinned to its collapsed width while closed, so a drag on the handle below resizes right instead
                        minSize={leftPanelOpen ? effectiveLeftMinSize : leftCollapsedPct}
                        maxSize={leftPanelOpen ? effectiveLeftMaxSize : leftCollapsedPct}
                        collapsedSize={leftCollapsedPct}
                        onCollapse={() => {
                            setLeftPanelOpen(false);
                            onLeftPanelCollapse?.(true);
                        }}
                        onExpand={() => {
                            setLeftPanelOpen(true);
                            onLeftPanelCollapse?.(false);
                        }}
                        onResize={handleLeftResize}
                    >
                        <SideBar
                            title={leftPanelHeader}
                            expanded={leftPanelOpen}
                            onToggle={onLeftPanelToggle}
                            expandedIcon={leftPanelExpandedIcon}
                            collapsedIcon={leftPanelCollapsedIcon}
                            showCollapseButton={isLeftCollapsible}
                            isForConditionOverview={isForConditionOverview}
                            conditionIndicesView={conditionIndicesView}
                            setConditionIndicesView={setConditionIndicesView}
                            requestHierarchyData={requestHierarchyData}
                            lastUpdatedTimestamp={lastUpdatedTimestamp}
                            assetsLastUpdatedTimestamp={assetsLastUpdatedTimestamp}
                            activeTab={activeTab}
                            contentPadding={leftPanelContentPadding}
                        >
                            {leftContent}
                        </SideBar>
                    </Panel>

                    {centerContent &&
                        <>
                            <ResizeHandle className={`ResizeHandle2 ${leftPanelOpen ? '' : 'd-none'}`} />
                            <Panel
                                id='center-panel'
                                // Pinned to its collapsed width while closed (see left panel above)
                                minSize={centerPanelOpen ? effectiveCenterMinSize : centerCollapsedPct}
                                maxSize={centerPanelOpen ? effectiveCenterMaxSize : centerCollapsedPct}
                                defaultSize={centerInitialSize}
                                collapsible={isCenterCollapsible}
                                collapsedSize={centerCollapsedPct}
                                onCollapse={() => {
                                    setCenterPanelOpen(false);
                                    onCenterPanelCollapse?.(true);
                                }}
                                onExpand={() => {
                                    setCenterPanelOpen(true);
                                    onCenterPanelCollapse?.(false);
                                }}
                                onResize={handleCenterResize}
                            >
                                <div className='middle-panel-wrapper'>
                                    {centerPanelOpen
                                        ? <div className='middle-panel-content'>{centerContent}</div>
                                        : <div className='middle-panel-collapsed'>
                                            <div className='column-sidebar-title collapsed'>{centerPanelHeader}</div>
                                            {isCenterCollapsible &&
                                                <button
                                                    type='button'
                                                    aria-label='Expand panel'
                                                    onClick={onCenterPanelToggle}
                                                    className='column-sidebar-toggle-button collapsed'
                                                >
                                                    {centerPanelCollapsedIcon}
                                                </button>
                                            }
                                        </div>
                                    }
                                    {isCenterCollapsible && centerPanelOpen &&
                                        <div className='middle-panel-footer'>
                                            <button
                                                type='button'
                                                aria-label='Collapse panel'
                                                onClick={onCenterPanelToggle}
                                                className='column-sidebar-toggle-button expanded'
                                            >
                                                {centerPanelExpandedIcon}
                                            </button>
                                        </div>
                                    }
                                </div>
                            </Panel>
                        </>
                    }

                    {rightContent &&
                        <>
                            <ResizeHandle className={`ResizeHandle2 ${centerPanelOpen ? '' : 'd-none'}`} />
                            <Panel
                                id='right-panel'
                                minSize={rightPanelMinSize}
                                defaultSize={rightInitialSize}
                                collapsible={false}
                                onResize={handleRightResize}
                            >
                                {rightContent}
                            </Panel>
                        </>
                    }
                </PanelGroup>
            </div>
        </div>
    );
});

ThreeColumnResizableLayoutV2.propTypes = {
    leftPanelDefaulSize: PropTypes.number,
    leftPanelCollapsedSize: PropTypes.number,
    leftPanelMinSize: PropTypes.number,
    centerPanelMinSize: PropTypes.number,
    centerPanelDefaultSize: PropTypes.number,
    centerPanelCollapsedSize: PropTypes.number,
    centerPanelCollapsedIcon: PropTypes.node,
    centerPanelExpandedIcon: PropTypes.node,
    leftPanelCollapsedIcon: PropTypes.node,
    leftPanelExpandedIcon: PropTypes.node,
    rightPanelMinSize: PropTypes.number,
    rightPanelDefaultSize: PropTypes.number,
    leftPanelMaxSize: PropTypes.number,
    centerPanelMaxSize: PropTypes.number,
    leftPanelMaxWidthPx: PropTypes.number,
    centerPanelMaxWidthPx: PropTypes.number,
    leftPanelHeader: PropTypes.node,
    centerPanelHeader: PropTypes.node,
    onLeftPanelCollapse: PropTypes.func,
    onCenterPanelCollapse: PropTypes.func,
    children: PropTypes.arrayOf(PropTypes.node),
    onResizeLeftPanel: PropTypes.func,
    onResizeCenterPanel: PropTypes.func,
    onResizeRightPanel: PropTypes.func,
    mainLayoutclass: PropTypes.string,
    isForHierarchyView: PropTypes.bool,
    isForConditionOverview: PropTypes.bool,
    conditionIndicesView: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setConditionIndicesView: PropTypes.func,
    requestHierarchyData: PropTypes.func,
    lastUpdatedTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    assetsLastUpdatedTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isCenterCollapsible: PropTypes.bool,
    isLeftCollapsible: PropTypes.bool,
    leftPanelContentPadding: PropTypes.string,
};

export default ThreeColumnResizableLayoutV2;
